import axios, { AxiosRequestConfig } from 'axios'
import {
  applyCaptchaUrl,
  loginUrl,
  loginWithoutCaptchaUrl,
  sendShortMessageCodeUrl,
  shortMessageLoginUrl,
  validateCaptchaUrl,
  authUrl,
  logoutUrl,
  validateShortMessageCaptchaUrl,
  sendShortMessageCodeUrlApply
} from './contant'
import { AccountType, LoginParams, SendMessageParams, ShortMessageLoginParams } from './interfaces/LoginParams'
import PhoneLogAble from './interfaces/PhoneLogAble'
import Response, { ResponseStatus } from '@api/Response'
import { EventEmitter } from 'events'
import LogAble from './interfaces/LogAble'
import ThirdParty from '@packages/authentication/plugins/third-party'

const Qs = require('querystring')

export { SendMessageParams, ShortMessageLoginParams, LoginParams, AccountType }

export enum GrantType {
  // 登录票类
  casTicket = 'cas_ticket',
  // 刷新票类
  refreshToken = 'refresh_token'
}

export class InitOptions {
  appKey: string
  ssoUrl: string
  authUrl: string
  service?: string
  prefix: string
  request: any
  thirdAuthUrl?: string
}

enum RequestMethod {
  POST = 'post',
  GET = 'get'
}

const grantType = 'grant_type'

/**
 * 授权需要的参数对象
 */
class AuthParams {
  // 登录票
  ticket: string;
  // 判别类型
  [grantType]: string
}

const refreshTokenKey = 'refresh_token'

/**
 * 刷新AToken的参数对象
 */
export class RefreshParams {
  [grantType]: string;
  [refreshTokenKey]: string
}

const request = async function(method: RequestMethod, url: string, config?: any) {
  return new Promise<Response<any>>((resolve, reject) => {
    const requestConfig = {
      url,
      method
    }
    Object.assign(requestConfig, config)
    axios.request(requestConfig).then(data => {
      const response = new Response()
      response.status = new ResponseStatus(data.data.code, data.data.message)
      response.data = data.data.data
      resolve(response)
    }, reject)
  })
}

class Authentication extends EventEmitter implements LogAble, PhoneLogAble {
  static refreshTokenKeyName = 'Refresh-Token'
  static accessTokenKeyName = 'Access-Token'
  static ticketGrantTicketIdKeyName = 'Grant-Ticket'
  static serviceTicketKIdKeyName = 'Service-Ticket'
  static rememberLoginInfoKeyName = 'Login-Info'
  options: InitOptions = new InitOptions()
  appAuthenticationStr = 'App-Authentication'
  service: string
  captchaToken: string
  captchaTicket: string
  shortMessageCaptchaToken: string
  shortMessageCaptchaTicket: string
  isCaptchaValidate = false
  isShortMessageCaptchaValidate = false
  validatedCaptchaValue = ''
  validatedShortMessageCaptchaValue = ''
  ticketGrantTicketId?: string = ''
  serviceTicket?: string = ''
  accessToken?: string = ''
  refreshToken?: string = ''
  expiresIn?: string = ''
  rememberLoginInfo: LoginParams

  constructor(options: InitOptions) {
    super()
    this.options = options
    // 如果没有提供 appKey 则报警
    this.service = options.service || `${location.protocol}//${location.hostname}`
    this.options.service = this.service

    localStorage.setItem(this.appAuthenticationStr, this.options.appKey)
    axios.interceptors.request.use((config: AxiosRequestConfig) => {
      const accessToken = this.getKey(Authentication.accessTokenKeyName)
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
      return config
    })
    this.setRememberLoginInfo()
    this.enableThirdParty()
  }

  thirdParty: ThirdParty

  /**
   * 允许第三方
   */
  enableThirdParty() {
    this.thirdParty = new ThirdParty(this.options)
  }

  static hasToken(prefix: string) {
    return localStorage.getItem(`${prefix}.${this.accessTokenKeyName}`)
  }

  setKey(key: string, value: string) {
    localStorage.setItem(`${this.options.prefix}.${key}`, value)
  }

  getKey(key: string): string {
    return localStorage.getItem(`${this.options.prefix}.${key}`) || ''
  }

  removeKey(key: string) {
    return localStorage.removeItem(`${this.options.prefix}.${key}`)
  }

  private setRememberLoginInfo() {
    const loginParams = new LoginParams()
    try {
      const result = JSON.parse(this.getKey(Authentication.rememberLoginInfoKeyName) || '{}')
      loginParams.password = result.password
      loginParams.account = result.account
      loginParams.longTerm = !!(result.password && result.account)
      this.rememberLoginInfo = loginParams
    } catch (e) {
      this.rememberLoginInfo = new LoginParams()
    }
  }

  /**
   * 执行登录操作
   * @param loginParams
   * @param url
   */
  private async doLogin(loginParams: LoginParams, url: string): Promise<Response<any>> {
    loginParams.service = this.service
    const result = await request(RequestMethod.POST, `${this.options.ssoUrl}${url}`, {
      data: loginParams
    })
    if (result.status.code !== 200) {
      return Promise.reject(result.status)
    }
    this.doRememberLoginInfo(loginParams)
    this.emit('login', true)
    this.storeTicket(result.data.serviceTicketId, result.data.ticketGrantTicketId)
    return result
  }

  storeTicket(serviceTicketId: string, ticketGrantTicketId: string) {
    this.serviceTicket = serviceTicketId
    this.ticketGrantTicketId = ticketGrantTicketId
    if (this.ticketGrantTicketId) {
      this.setKey(Authentication.ticketGrantTicketIdKeyName, this.ticketGrantTicketId)
    }
    if (this.serviceTicket) {
      this.setKey(Authentication.serviceTicketKIdKeyName, this.serviceTicket)
    }
  }

  /**
   * 通用登录，没有默认accountType
   * @param loginParams
   */
  async login(loginParams: LoginParams): Promise<Response<any>> {
    loginParams.captchaToken = this.captchaToken
    return this.doLogin(loginParams, loginUrl)
  }

  async loginWithoutCaptcha(loginParams: LoginParams): Promise<Response<any>> {
    return this.doLogin(loginParams, loginWithoutCaptchaUrl)
  }

  /**
   * 管理员登录accountType = 2
   * @param loginParams
   */
  async adminLogin(loginParams: LoginParams) {
    loginParams.accountType = AccountType.admin
    return this.login(loginParams)
  }

  /**
   * 客户域登录，默认accountType=1
   * @param loginParams
   */
  async customerLogin(loginParams: LoginParams) {
    loginParams.accountType = AccountType.customer
    return this.login(loginParams)
  }

  private doRememberLoginInfo(params: LoginParams) {
    if (params.longTerm) {
      this.rememberLoginInfo = params
      this.setKey(
        Authentication.rememberLoginInfoKeyName,
        JSON.stringify({
          account: params.account,
          password: params.password,
          longTerm: true
        })
      )
    } else {
      this.rememberLoginInfo = new LoginParams()
      this.removeKey(Authentication.rememberLoginInfoKeyName)
    }
  }

  /**
   * 登出
   */
  async logout() {
    const result = await axios.get(`${this.options.ssoUrl}${logoutUrl}/${this.getTicketGrantTicketId()}`)
    const isLogOutSuccess = result.data.code === 200
    if (isLogOutSuccess) {
      this.emit('logout', isLogOutSuccess)
      this.removeKey(Authentication.ticketGrantTicketIdKeyName)
      this.removeKey(Authentication.serviceTicketKIdKeyName)
      this.removeKey(Authentication.accessTokenKeyName)
      this.removeKey(Authentication.refreshTokenKeyName)
    }
    return isLogOutSuccess
  }

  /**
   * 获取验证码
   * // 时效性30秒？
   */
  async applyCaptcha() {
    const result = await request(RequestMethod.GET, `${this.options.authUrl}${applyCaptchaUrl}`)
    this.captchaTicket = result.data.captchaTicket
    return result
  }

  /**
   * 验证验证码
   * @param captchaValue
   */
  async validateCaptcha(captchaValue: string) {
    if (captchaValue === this.validatedCaptchaValue) {
      return this.isCaptchaValidate
    }
    const result = await request(
      RequestMethod.GET,
      `${this.options.authUrl}${validateCaptchaUrl}/${this.captchaTicket}/${captchaValue}`
    )
    this.captchaToken = result.data.captchaToken
    this.isCaptchaValidate = result.data.pass
    if (!this.isCaptchaValidate) {
      return Promise.reject(result.data)
    }
    this.validatedCaptchaValue = captchaValue
    return result
  }

  /**
   * 验证短信验证码
   * @param captchaValue
   */
  async validateShortMessageCaptcha(captchaValue: string) {
    if (captchaValue === this.validatedShortMessageCaptchaValue) {
      return this.isShortMessageCaptchaValidate
    }
    const result = await request(
      RequestMethod.GET,
      `${this.options.authUrl}${validateShortMessageCaptchaUrl}/${this.shortMessageCaptchaTicket}/${captchaValue}`
    )
    this.shortMessageCaptchaToken = result.data.captchaToken
    this.isShortMessageCaptchaValidate = result.data.pass
    if (!this.isShortMessageCaptchaValidate) {
      return Promise.reject(result.data)
    }
    this.validatedShortMessageCaptchaValue = captchaValue
    return result
  }

  /**
   * 发送短信验证码
   * @param sendMessageParams
   */
  async sendMessage(sendMessageParams: SendMessageParams): Promise<any> {
    sendMessageParams.service = this.service
    sendMessageParams.captchaToken = this.captchaToken
    return request(RequestMethod.POST, `${this.options.ssoUrl}${sendShortMessageCodeUrl}`, {
      data: sendMessageParams
    })
  }

  /**
   * 发送短信验证码
   * @param sendMessageParams
   */
  async sendMessageNoValidate(sendMessageParams: SendMessageParams): Promise<any> {
    sendMessageParams.service = this.service
    sendMessageParams.captchaToken = this.captchaToken
    const result = await request(RequestMethod.POST, `${this.options.authUrl}${sendShortMessageCodeUrlApply}`, {
      data: sendMessageParams
    })
    this.shortMessageCaptchaTicket = result.data.captchaTicket
    return result
  }

  /**
   * 短信验证码登录
   * @param shortMessageLoginParams
   */
  async shortMessageLogin(shortMessageLoginParams: ShortMessageLoginParams) {
    const result = await request(RequestMethod.POST, `${this.options.ssoUrl}${shortMessageLoginUrl}`, {
      data: shortMessageLoginParams
    })
    if (!result.status.isSuccess()) {
      return Promise.reject(result.status)
    }
    this.storeTicket(result.data.serviceTicketId, result.data.ticketGrantTicketId)
    return result
  }

  /**
   * 登录完成后需要调用的授权接口
   * @param params
   */
  async auth(params?: AuthParams) {
    const result = await axios.post(
      `${this.options.authUrl}${authUrl}`,
      Qs.stringify(
        Object.assign(
          {
            [grantType]: GrantType.casTicket,
            ticket: this.getKey(Authentication.serviceTicketKIdKeyName)
          },
          params
        )
      ),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    this.accessToken = result.data.data.access_token
    this.refreshToken = result.data.data.refresh_token
    this.expiresIn = result.data.data.expires_in
    if (this.refreshToken) {
      this.setRefreshToken(this.refreshToken)
    }
    if (this.accessToken) {
      this.setAccessToken(this.accessToken)
    }
    return result
  }

  /**
   * 当系统发现AT过期后，需要使用RT去获取新的AT
   */
  async refresh(params?: RefreshParams) {
    const result = await axios.post(
      `${this.options.authUrl}${authUrl}`,
      Qs.stringify(
        Object.assign(
          {
            [grantType]: GrantType.refreshToken,
            [refreshTokenKey]: this.getRefreshToken()
          },
          params
        )
      ),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    if (result.data.code === 200) {
      this.refreshToken = result.data.data.access_token
      if (this.refreshToken) {
        this.setRefreshToken(result.data.data.access_token)
      }
    } else {
      return Promise.reject(result.data)
    }
    return result
  }

  /**
   * 重新生成服务票
   */
  async applyServiceTicket() {
    const result = await axios.post(
      `${this.options.ssoUrl}/web/rest/applyServiceTicket`,
      {
        service: this.service,
        ticketGrantTicketId: this.getKey(Authentication.ticketGrantTicketIdKeyName)
      },
      {
        headers: {
          'Content-Type': 'application/json;charset=utf-8'
        }
      }
    )
    if (result.data.code !== 200) {
      return Promise.reject(result.data)
    }
    this.setKey(Authentication.serviceTicketKIdKeyName, result.data.data.serviceTicketId)
    return result
  }

  getRequestHeader() {
    return {
      Authorization: `Bearer ${this.getAccessToken()}`,
      [this.appAuthenticationStr]: `Basic ${this.options.appKey}`
    }
  }

  async wxTokenLogin(token: string) {
    const result = await this.thirdParty.login(token)
    this.storeTicket(result.serviceTicketId, result.ticketGrantTicketId)
    await this.auth()
  }

  /**
   * 获取accessToken
   */
  getAccessToken() {
    return this.getKey(Authentication.accessTokenKeyName)
  }

  /**
   * 获取refreshToken
   */
  getRefreshToken() {
    return this.getKey(Authentication.refreshTokenKeyName)
  }

  /**
   * 设置refreshToken
   * @param value
   */
  setRefreshToken(value: string) {
    this.setKey(Authentication.refreshTokenKeyName, value)
  }

  /**
   * 设置accessToken
   * @param value
   */
  setAccessToken(value: string) {
    this.setKey(Authentication.accessTokenKeyName, value)
  }

  getTicketGrantTicketId() {
    return this.getKey(Authentication.ticketGrantTicketIdKeyName)
  }
}

export default Authentication
